import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, FolderKanban, Archive, Lock, Search, X } from "lucide-react";
import { api, formatApiError, PROJECT_STATUS_LABELS, PROJECT_LIFECYCLE_LABELS, PROJECT_LIFECYCLE_STYLE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export default function Projects() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState(null);
  const [workspaces, setWorkspaces] = useState([]);
  const [error, setError] = useState("");
  const [showCreate, setShowCreate] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ workspace_id: "", name: "", description: "", confidential: false, project_status: "planning" });
  const [wsMembers, setWsMembers] = useState([]);
  const [participants, setParticipants] = useState([]); // [{user_id, project_role}]

  const [fWorkspace, setFWorkspace] = useState("");
  const [fView, setFView] = useState("");
  const [fConfidential, setFConfidential] = useState("");
  const [fParticipating, setFParticipating] = useState(false);
  const [query, setQuery] = useState("");

  const creatableWs = useMemo(
    () => workspaces.filter((w) => w.status !== "archived"
      && (w.type === "department" || w.type === "interdepartmental")
      && (isAdmin || (w.manager_ids || []).includes(user?.id))),
    [workspaces, isAdmin, user]
  );

  const selectedWs = useMemo(() => workspaces.find((w) => w.id === form.workspace_id), [workspaces, form.workspace_id]);
  const isEISelected = selectedWs?.type === "interdepartmental";

  const load = async () => {
    try {
      const params = {};
      if (fWorkspace) params.workspace_id = fWorkspace;
      if (fView) params.view = fView;
      if (fConfidential) params.confidential = fConfidential;
      if (fParticipating) params.participating = "true";
      const { data } = await api.get("/projects", { params });
      setItems(data);
    } catch (e) { setError(formatApiError(e)); }
  };

  useEffect(() => { api.get("/workspaces").then(({ data }) => setWorkspaces(data)).catch(() => {}); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [fWorkspace, fView, fConfidential, fParticipating]);

  // Default workspace when opening the create modal
  useEffect(() => {
    if (showCreate && !form.workspace_id && creatableWs.length) setForm((f) => ({ ...f, workspace_id: creatableWs[0].id }));
    /* eslint-disable-next-line */
  }, [showCreate, creatableWs]);

  // Load eligible participants for the selected department
  useEffect(() => {
    if (!showCreate || !form.workspace_id) { setWsMembers([]); return; }
    const ws = workspaces.find((w) => w.id === form.workspace_id);
    api.get(`/workspaces/${form.workspace_id}/members`)
      .then(({ data }) => {
        if (ws?.type === "interdepartmental") {
          setWsMembers((data.managers || []).filter((m) => m.status === "active"));
        } else {
          setWsMembers([...(data.managers || []), ...(data.members || [])]);
        }
      })
      .catch(() => setWsMembers([]));
    setParticipants([]);
  }, [showCreate, form.workspace_id, workspaces]);

  const toggleParticipant = (uid) => {
    setParticipants((prev) => prev.some((p) => p.user_id === uid)
      ? prev.filter((p) => p.user_id !== uid)
      : [...prev, { user_id: uid, project_role: "member" }]);
  };
  const setParticipantRole = (uid, role) => setParticipants((prev) => prev.map((p) => p.user_id === uid ? { ...p, project_role: role } : p));

  const filtered = useMemo(() => {
    if (!items) return null;
    const q = query.trim().toLowerCase();
    return q ? items.filter((p) => p.name.toLowerCase().includes(q)) : items;
  }, [items, query]);

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const { data } = await api.post("/projects", { ...form, participants });
      toast.success("Projeto criado.");
      setShowCreate(false);
      setForm({ workspace_id: "", name: "", description: "", confidential: false, project_status: "planning" });
      setParticipants([]); setWsMembers([]);
      navigate(`/projetos/${data.id}`);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="space-y-6" data-testid="projects-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Trabalho</div>
          <h1 className="font-heading text-3xl sm:text-4xl font-semibold tracking-tight text-slate-900 mt-2">Projetos</h1>
          <p className="text-slate-500 mt-1">Projetos pertencem a um departamento e têm participantes explícitos.</p>
        </div>
        {creatableWs.length > 0 && (
          <button onClick={() => { setForm((f) => ({ ...f, workspace_id: creatableWs[0].id })); setShowCreate(true); }}
            data-testid="projects-create-btn"
            className="inline-flex items-center gap-2 h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800">
            <Plus className="w-4 h-4" /> Novo projeto
          </button>
        )}
      </div>

      {error && <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{error}</div>}

      <div className="flex flex-wrap items-center gap-3" data-testid="projects-filters">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Pesquisar por nome" data-testid="projects-search"
            className="h-9 pl-9 pr-3 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
        </div>
        <select value={fWorkspace} onChange={(e) => setFWorkspace(e.target.value)} data-testid="filter-workspace"
          className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm">
          <option value="">Todos os espaços</option>
          {workspaces.filter((w) => w.type === "department" || w.type === "interdepartmental").map((w) => <option key={w.id} value={w.id}>{w.name}{w.type === "interdepartmental" ? " (E.I.)" : ""}</option>)}
        </select>
        <select value={fView} onChange={(e) => setFView(e.target.value)} data-testid="filter-status"
          className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm">
          <option value="">Ativos</option>
          <option value="planning">Planejamento</option>
          <option value="in_progress">Em andamento</option>
          <option value="paused">Pausado</option>
          <option value="completed">Concluído</option>
          <option value="archived">Arquivado</option>
        </select>
        <select value={fConfidential} onChange={(e) => setFConfidential(e.target.value)} data-testid="filter-confidential"
          className="h-9 rounded-lg border border-slate-200 bg-white px-2 text-sm">
          <option value="">Confidencialidade</option>
          <option value="true">Confidenciais</option>
          <option value="false">Não confidenciais</option>
        </select>
        <label className="inline-flex items-center gap-2 text-sm text-slate-600">
          <input type="checkbox" checked={fParticipating} onChange={(e) => setFParticipating(e.target.checked)} data-testid="filter-participating" />
          Só onde participo
        </label>
      </div>

      {filtered === null ? (
        <div className="text-slate-500">Carregando...</div>
      ) : filtered.length === 0 ? (
        <div className="bg-white border border-dashed border-slate-300 rounded-xl p-12 text-center" data-testid="projects-empty">
          <FolderKanban className="w-10 h-10 text-slate-300 mx-auto" strokeWidth={1.5} />
          <h3 className="font-heading text-lg font-semibold mt-3">Nenhum projeto acessível</h3>
          <p className="text-sm text-slate-500 mt-1">Crie um projeto ou peça para ser incluído em um.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="projects-grid">
          {filtered.map((p) => (
            <Link key={p.id} to={`/projetos/${p.id}`} data-testid={`project-card-${p.id}`}
              className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 transition-all hover:shadow-md hover:border-slate-300 hover:-translate-y-0.5">
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-lg bg-slate-100 grid place-items-center">
                  <FolderKanban className="w-5 h-5 text-slate-700" strokeWidth={1.5} />
                </div>
                <div className="flex items-center gap-1 flex-wrap justify-end">
                  <span className={`inline-flex items-center text-xs px-2 py-0.5 rounded-md ${PROJECT_LIFECYCLE_STYLE[p.project_status] || "bg-slate-100 text-slate-600"}`} data-testid={`project-lifecycle-${p.id}`}>{PROJECT_LIFECYCLE_LABELS[p.project_status] || "Em andamento"}</span>
                  {p.confidential && <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md bg-rose-50 text-rose-700"><Lock className="w-3 h-3" /> Confidencial</span>}
                  {p.status === "archived" && <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-md bg-slate-100 text-slate-600"><Archive className="w-3 h-3" /> {PROJECT_STATUS_LABELS.archived}</span>}
                </div>
              </div>
              <h3 className="font-heading text-lg font-semibold tracking-tight mt-4">{p.name}</h3>
              <p className="text-xs text-slate-500 mt-1">{p.workspace_name}</p>
              <p className="text-sm text-slate-500 mt-1 line-clamp-2 min-h-[2.5rem]">{p.description || "Sem descrição."}</p>
              <div className="mt-4">
                <div className="flex items-center justify-between text-xs mb-1.5">
                  <span className="text-slate-600 font-medium" data-testid={`project-progress-text-${p.id}`}>{p.done_tasks ?? 0} de {p.total_tasks ?? 0} tarefas concluídas</span>
                  <span className="text-slate-900 font-semibold">{p.progress ?? 0}%</span>
                </div>
                <div className="h-2 rounded-full bg-slate-100 overflow-hidden" data-testid={`project-progress-bar-${p.id}`}>
                  <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: `${p.progress ?? 0}%` }} />
                </div>
              </div>
              <div className="mt-3 flex items-center justify-between gap-2 text-xs">
                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md ${(p.essential_pending ?? 0) > 0 ? "bg-amber-50 text-amber-700" : "bg-slate-100 text-slate-500"}`} data-testid={`project-essential-${p.id}`}>
                  {p.essential_pending ?? 0} {p.essential_pending === 1 ? "essencial pendente" : "essenciais pendentes"}
                </span>
                {p.active_sprint
                  ? <span className="inline-flex items-center gap-1.5 text-slate-600 truncate"><span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: p.active_sprint.color || "#64748b" }} /><span className="truncate">{p.active_sprint.name}</span></span>
                  : <span className="text-slate-400">{p.participants.length} participantes</span>}
              </div>
            </Link>
          ))}
        </div>
      )}

      {showCreate && (
        <div className="fixed inset-0 z-50 bg-slate-900/30 grid place-items-center p-4" onClick={() => setShowCreate(false)} data-testid="create-project-modal">
          <form onClick={(e) => e.stopPropagation()} onSubmit={submit} className="bg-white rounded-xl border border-slate-200 shadow-lg w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden">
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 shrink-0">
              <h2 className="font-heading text-xl font-semibold tracking-tight">Novo projeto</h2>
              <button type="button" onClick={() => setShowCreate(false)} data-testid="new-project-close" className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-5 h-5" /></button>
            </div>
            <div className="px-6 py-4 space-y-4 overflow-y-auto" data-testid="new-project-body">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Espaço</label>
              <select required value={form.workspace_id} onChange={(e) => setForm({ ...form, workspace_id: e.target.value })} data-testid="new-project-workspace"
                className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm">
                {creatableWs.map((w) => <option key={w.id} value={w.id}>{w.name}{w.type === "interdepartmental" ? " · Interdepartamental" : ""}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Nome</label>
              <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="new-project-name"
                className="w-full h-10 rounded-lg border border-slate-200 px-3 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Descrição</label>
              <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="new-project-description"
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-900" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Status</label>
              <select value={form.project_status} onChange={(e) => setForm({ ...form, project_status: e.target.value })} data-testid="new-project-status"
                className="w-full h-10 rounded-lg border border-slate-200 bg-white px-3 text-sm">
                {Object.entries(PROJECT_LIFECYCLE_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <label className="inline-flex items-center gap-2 text-sm text-slate-700">
              <input type="checkbox" checked={form.confidential} onChange={(e) => setForm({ ...form, confidential: e.target.checked })} data-testid="new-project-confidential" />
              Projeto confidencial
            </label>
            <div data-testid="new-project-participants">
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Participantes</label>
              {wsMembers.length === 0 ? (
                <p className="text-xs text-slate-400">{isEISelected ? "Nenhum gerente elegível neste espaço interdepartamental." : "Nenhum usuário elegível neste departamento."}</p>
              ) : (
                <div className="max-h-44 overflow-y-auto border border-slate-200 rounded-lg divide-y divide-slate-100">
                  {wsMembers.map((m) => {
                    const sel = participants.find((p) => p.user_id === m.id);
                    return (
                      <div key={m.id} className="flex items-center gap-2 px-3 py-2 text-sm" data-testid={`participant-option-${m.id}`}>
                        <input type="checkbox" checked={!!sel} onChange={() => toggleParticipant(m.id)} data-testid={`participant-check-${m.id}`} />
                        <span className="flex-1 min-w-0 truncate">{m.name} <span className="text-slate-400">· {m.email}</span></span>
                        {sel && !isEISelected && (
                          <select value={sel.project_role} onChange={(e) => setParticipantRole(m.id, e.target.value)} data-testid={`participant-role-${m.id}`} className="h-7 rounded-md border border-slate-200 bg-white px-1 text-xs">
                            <option value="member">Membro</option>
                            <option value="viewer">Visualizador</option>
                          </select>
                        )}
                        {sel && isEISelected && <span className="text-xs text-slate-400">Gerente</span>}
                      </div>
                    );
                  })}
                </div>
              )}
              <p className="text-[10px] text-slate-400 mt-1">{isEISelected ? "Apenas gerentes ativos participantes do E.I. podem ser incluídos." : "Você poderá ajustar os participantes depois em Configurações."}</p>
            </div>
            </div>
            <div className="flex justify-end gap-2 px-6 py-4 border-t border-slate-200 shrink-0 bg-white">
              <button type="button" onClick={() => setShowCreate(false)} className="h-10 px-4 rounded-lg border border-slate-200 text-sm">Cancelar</button>
              <button type="submit" disabled={submitting} data-testid="new-project-submit"
                className="h-10 px-4 rounded-lg bg-slate-900 text-white text-sm font-medium hover:bg-slate-800 disabled:opacity-50">
                {submitting ? "Criando..." : "Criar projeto"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
